import { Injectable, SecurityContext, OnDestroy } from '@angular/core';
import { DefaultDataService } from './default-data.service';
import { File } from '../models/file';
import { Subject } from '../models/subject';
import { HttpClient } from '@angular/common/http';
import { messageService } from './messages/message.module';
import { DomSanitizer } from '@angular/platform-browser';
import { EventService } from './event.service';
import { SubjectService } from './subject.service';
import { Observable, ReplaySubject, Subscription, filter, switchMap, take, timeout, timer, mergeMap } from 'rxjs';
import * as messageBoxComponent from 'app/components/messageBox/messageBox.component';
import { MatDialog } from '@angular/material/dialog';
import { User } from '../models/user';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class HyperLawService implements OnDestroy {
  hyperLawLicenceValid: boolean;
  hyperLawLicenceValid$: Subscription;
  userHasCasePrepAccess$: Subscription;
  moduleActive: ReplaySubject<boolean> = new ReplaySubject(1, 100);
  configValid: ReplaySubject<boolean> = new ReplaySubject(1, 100);
  disableUI: ReplaySubject<boolean> = new ReplaySubject(1, 100);
  caseAddedForSubjectUid: ReplaySubject<string> = new ReplaySubject(1, 100);

  constructor(
    private http: HttpClient,
    private defaultDataService: DefaultDataService,
    private eventService: EventService,
    private subjectService: SubjectService,
    private userService: UserService,
    private sanitizer: DomSanitizer,
    public dialog: MatDialog
  ) {
    this.hyperLawLicenceValid$ = this.eventService.onUpdateHyperLawLicenceValid.subscribe(
      (hyperLawLicenceValid: boolean) => {
        this.hyperLawLicenceValid = hyperLawLicenceValid;
        this.moduleActive.next(this.isModuleActive());
        this.configValid.next(this.isConfigValid());
      }
    );

    this.userHasCasePrepAccess$ = this.eventService.onChangeUser.subscribe((_user: User) => {
      this.configValid.next(this.isConfigValid());
    });
  }

  isConfigValid(): boolean {
    return (
      this.defaultDataService.hyperLawLicenceValid &&
      this.defaultDataService.enableCasePreparationFeatures &&
      this.defaultDataService.hyperLawApiKey.length > 0 &&
      this.defaultDataService.hyperLawCustomerExternalRef.length > 0 &&
      this.userService.getCurrentUser()?.hasCasePrepAccess
    );
  }

  isModuleActive(): boolean {
    return this.defaultDataService.hyperLawLicenceValid && this.defaultDataService.enableCasePreparationFeatures;
  }

  ngOnDestroy() {
    if (this.hyperLawLicenceValid$) {
      this.hyperLawLicenceValid$.unsubscribe();
    }
  }

  getCase(subjectUid: string): Observable<HyperLawCase> {
    return this.http.get(`api/hyperlaw/case/${subjectUid}`).pipe();
  }

  getCaseAndUpdateSubject(subject: Subject): void {
    this.http
      .get<HyperLawCase>(
        `api/hyperlaw/case/${subject.uid}/${btoa('\\subject\\' + subject.uid)}/${
          subject?.hasCasePreparationFile || false
        }`
      )
      .subscribe({
        next: (result: HyperLawCase) => {
          this.eventService.filesUpdated();
          subject.hasCasePreparationFile = !!result;
          this.eventService.updateSubject(subject);
          if (subject.hasCasePreparationFile){
            this.caseAddedForSubjectUid.next(subject.uid);
          }
        },
        error: () => {},
      });
  }

  openCase(subject: Subject): void {
    this.getCase(subject.uid).subscribe((result: HyperLawCase) => {
      const url = (result.url || 'https://www.axiomhyperlaw.co.uk/api').replace('/api','');
      this.http.get<string>(`api/hyperlaw/user/login/token`).subscribe({
        next: (token: string) => {
          window.open(
            <string>(
              this.sanitizer.sanitize(
                SecurityContext.URL,
                `${url}/Login/Auto?token=${token}&url=${result.internalCaseURI}`
              )
            )
          );
        },
        error: () => {
          messageService.error('Failed to open case preparation file');
        },
      });
    });
  }

  addCase(subject: Subject, files?: File[]) {
    this.disableUI.next(true);

    this.http.post('api/hyperlaw/case', new HyperLawCase(subject)).subscribe({
      next: () => {
        timer(0, 2500)
          .pipe(
            switchMap(() => this.getCase(subject.uid)),
            filter((result: HyperLawCase) => !!result?.internalCaseId),
            take(1),
            timeout(60000)
          )
          .subscribe({
            next: (hlc: HyperLawCase) => {
              this.disableUI.next(false);
              subject.hasCasePreparationFile = true;
              messageService.success('Case preparation file created');
              this.eventService.updateSubject(subject);
              this.caseAddedForSubjectUid.next(subject.uid);

              if (files?.length > 0) {
                this.addDocuments(subject, hlc.internalCaseId, files);
              }

              this.subjectService
                .getUserSecurity(subject)
                .pipe(take(1))
                .subscribe((userIds: number[]) => {
                  const users: User[] = userIds
                    ? userIds.map((i) => this.userService.getUserById(i))
                    : this.userService.getAllActiveUsersList();

                  this.addUsersToCase(
                    hlc.internalCaseId,
                    users.map((i) => i.email)
                  );
                });
            },
            error: () => this.disableUI.next(false),
          });
      },
      error: () => {
        this.disableUI.next(false);
        messageService.error('Failed to create case preparation file');
      },
    });
  }

  deleteCase(subject: Subject): void {
    this.disableUI.next(true);
    this.getCase(subject.uid).subscribe({
      next: (hlc: HyperLawCase) => {
        this.http
          .delete(`api/hyperlaw/case/${subject.uid}/${hlc.internalCaseId}/${btoa('\\subject\\' + subject.uid)}`)
          .subscribe({
            next: () => {
              timer(0, 2500)
                .pipe(
                  switchMap(() => this.http.get(`api/hyperlaw/case/${subject.uid}`)),
                  filter((result: HyperLawCase) => !result),
                  take(1),
                  timeout(60000)
                )
                .subscribe({
                  next: () => {
                    this.disableUI.next(false);
                    subject.hasCasePreparationFile = false;
                    this.eventService.updateSubject(subject);
                    this.eventService.filesUpdated();
                    messageService.success('Case preparation file deleted');
                  },
                  error: () => {
                    this.disableUI.next(false);
                  },
                });
            },
            error: () => {
              this.disableUI.next(false);
              messageService.error('Failed to delete case preparation file');
            },
          });
      },
      error: () => {
        this.disableUI.next(false);
        messageService.error('Failed to delete case preparation file');
      },
    });
  }

  addUsersToCase(internalCaseId: string, userEmails: string[]) {
    this.http.post(`api/hyperlaw/case/${internalCaseId}/addUsers`, userEmails).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  removeUsersFromCase(internalCaseId: string, userEmails: string[]) {
    this.http.post(`api/hyperlaw/case/${internalCaseId}/removeUsers`, userEmails).subscribe({
      next: () => {},
      error: () => {
        messageService.error('Failed to remove user(s) from case preparation file');
      },
    });
  }

  addDocuments(subject: Subject, internalCaseId: string, files: File[]): void {
    const uploadInfo: UploadInfo[] = [];
    for (const file of files) {
      uploadInfo.push(new UploadInfo(file));
    }

    this.http.post<void>(`api/hyperlaw/case/${internalCaseId}/addDocuments`, uploadInfo).subscribe({
      next: () => {
        this.eventService.filesUpdated();
      },
      error: () => {},
    });
  }

  deleteDocuments(subjectUid: string, files: File[]): Observable<string[]> {
    const uploadInfo: UploadInfo[] = [];
    for (const file of files) {
      uploadInfo.push(new UploadInfo(file));
    }

    return this.getCase(subjectUid).pipe(
      mergeMap((hlc: HyperLawCase) => {
        this.eventService.filesUpdated();
        return this.http.post<string[]>(`api/hyperlaw/case/${hlc.internalCaseId}/deleteDocuments`, uploadInfo);
      })
    );
  }

  exportToCaseBtnClicked(subjectId: string, files: File[]): void {
    if (!this.fileExtensionsAllowed(files)) {
      messageService.error(`One or more files could not be exported. Only files with the 
      following extension are allowed: pdf, jpg, jpeg, png, zip, txt, doc, docx, xlsx.`);
    } else {
      this.subjectService.getFullSubject(subjectId).subscribe((subject: Subject) => {
        if (!this.fileExtensionsAllowed(files)) {
          messageService.error(`One or more files could not be exported. Only files with the 
          following extension are allowed: pdf, jpg, jpeg, png, zip, txt, doc, docx, xlsx.`);
        } else if (!subject.hasCasePreparationFile) {
          this.addCase(subject, files);
        } else {
          const totalAlreadyExported = files.filter((file) => file?.casePrepExportStatus == 20).length;
          if (totalAlreadyExported == 0) {
            this.getCase(subject.uid).subscribe((hlc: HyperLawCase) => {
              if (hlc?.internalCaseId) {
                this.addDocuments(subject, hlc.internalCaseId, files);
              }
            });
          } else if (totalAlreadyExported == files.length) {
            messageService.error('All selected files have already been exported');
          } else if (totalAlreadyExported == 0) {
            this.dialog
              .open(messageBoxComponent.MessageBoxComponent, {
                data: new messageBoxComponent.MessageBoxParameters(
                  `One or more files selected have already been exported. 
                  These files will not be exported to the case preparation file. 
                  Do you wish to continue?`,
                  'Confirm',
                  messageBoxComponent.MessageBoxType.Confirm,
                  true
                ),
                disableClose: false,
                autoFocus: false,
              })
              .afterClosed()
              .subscribe((result) => {
                if (result === 'confirm') {
                  this.getCase(subject.uid).subscribe((hlc: HyperLawCase) => {
                    if (hlc?.internalCaseId) {
                      this.addDocuments(subject, hlc.internalCaseId, files);
                    }
                  });
                }
              });
          }
        }
      });
    }
  }

  fileExtensionsAllowed(files: File[]): boolean {
    return (
      files.findIndex(
        (i) =>
          !i.isDirectory &&
          !(
            i.fileName.endsWith('.pdf') ||
            i.fileName.endsWith('.jpg') ||
            i.fileName.endsWith('.jpeg') ||
            i.fileName.endsWith('.png') ||
            i.fileName.endsWith('.zip') ||
            i.fileName.endsWith('.txt') ||
            i.fileName.endsWith('.doc') ||
            i.fileName.endsWith('.docx') ||
            i.fileName.endsWith('.xlsx')
          )
      ) === -1
    );
  }
}

export enum CasePrepExportStatus {
  Failure = 10,
  Success = 20,
}

export class UploadInfo {
  name: string;
  path: string;
  isDirectory: boolean;
  casePrepExportStatus: number;
  uid: string;

  constructor(file: File) {
    this.name = file.fileName;
    this.path = file.path;
    this.isDirectory = file.isDirectory;
    this.casePrepExportStatus = file.casePrepExportStatus;
    this.uid = file.uid;
  }
}

export class HyperLawCase {
  constructor(subject: Subject) {
    this.caseFileId = subject.uid;
    this.matter = subject.reference;
    this.type = 'Prosecution';
    this.client = new Client(subject);
    this.externalCaseId = subject.reference;
  }

  customerExternalRef?: string;
  userExternalRef?: string;
  caseFileId?: string;
  internalCaseId?: string;
  matter?: string;
  type?: any;
  dateOpened?: string;
  department?: string;
  notes?: string;
  isPrivate?: boolean;
  client?: Client;
  otherParties?: any[];
  sectionTemplateName?: string;
  sections?: any[];
  documentTypeTemplateName?: string;
  documentTypes?: any[];
  externalCaseId?: string;
  externalCaseURI?: string;
  internalCaseURI?: string;
  url?: string;
}

export class Client {
  constructor(subject: Subject) {
    this.name = subject?.subjectOrganisations[0]?.organisation?.name || '';
  }

  name?: string;
  email?: string;
  phoneNumber?: string;
  address?: string;
  postCode?: string;
  contact: any;
}
